<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('settings.settingsLabel') }}</span>
        <h3 class="page-title">{{ $t('shippers.editShipper') }}</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/ustawienia/spedytorzy')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>
        <d-button theme="success" @click.prevent="validateForm">{{ $t('ui.saveChanges') }}</d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <d-card class="mb-5">
        <d-tabs v-sticky-tabs card>
          <!-- Dane ogólne -->
          <d-tab
            :title="$t('ui.generalInfo')"
            active>
            <h4 class="mb-3 pb-3 border-bottom">
              {{ $t('ui.generalInfo') }}
            </h4>

            <validation-observer ref="shipper-edit-form" tag="form">
              <div class="form-group">
                <label for="name">
                  {{ $t('shippers.shipperName') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="name"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="name" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="name">
                  {{ $t('ui.code') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="code"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="code" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </validation-observer>

            <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
              {{ $t('ui.dataLoading') }}&hellip;
            </div>

            <d-alert
              v-if="loadError"
              show
              theme="warning">
              {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
            </d-alert>
          </d-tab>

          <d-tab :title="$t('price.prices')">
            <h4 class="pb-3 mb-0 border-bottom d-flex">
              {{ $t('price.prices') }}

              <d-button
                class="ml-auto"
                theme="success"
                @click.prevent="addPrice">
                <i class="material-icons">add</i>
                {{ $t('price.addPrice') }}
              </d-button>
            </h4>

            <table class="table mt-0 mb-0 full-width">
              <thead class="bg-light">
                <tr>
                  <th scope="col" class="border-0">{{ $t('settings.country.country') }}</th>
                  <th scope="col" class="border-0">{{ $t('price.priceFlat') }}</th>
                  <th scope="col" class="border-0">{{ $t('price.price25pcs') }}</th>
                  <th scope="col" class="border-0">{{ $t('price.priceFirstBox') }}</th>
                  <th scope="col" class="border-0">{{ $t('price.priceSecondBox') }}</th>
                  <th scope="col" class="border-0">{{ $t('price.priceNextBox') }}</th>
                  <th scope="col" class="border-0">{{ $t('settings.currency.currency') }}</th>
                  <th scope="col" class="border-0">{{ $t('ui.actions') }}</th>
                </tr>
              </thead>
              <tbody v-if="loadedPrices">
                <tr
                  v-for="(price, index) of prices"
                  :key="'price-' + index">
                  <td class="align-middle">
                    {{ getCountry(price.country_id) }}
                  </td>
                  <td class="align-middle">
                    <template v-if="price.price">
                      {{ Number(price.price).toFixed(2) }}
                    </template>
                    <template v-else>
                      &mdash;
                    </template>
                  </td>
                  <td class="align-middle">
                    <template v-if="price.price_pcs">
                      {{ Number(price.price_pcs).toFixed(2) }}
                    </template>
                    <template v-else>
                      &mdash;
                    </template>
                  </td>
                  <td class="align-middle">
                    <template v-if="price.price_first_box">
                      {{ Number(price.price_first_box).toFixed(2) }}
                    </template>
                    <template v-else>
                      &mdash;
                    </template>
                  </td>
                  <td class="align-middle">
                    <template v-if="price.price_second_box">
                      {{ Number(price.price_second_box).toFixed(2) }}
                    </template>
                    <template v-else>
                      &mdash;
                    </template>
                  </td>
                  <td class="align-middle">
                    <template v-if="price.price_next_box">
                      {{ Number(price.price_next_box).toFixed(2) }}
                    </template>
                    <template v-else>
                      &mdash;
                    </template>
                  </td>
                  <td class="align-middle">
                    {{ getCurrency(price.currency_id) }}
                  </td>
                  <td
                    class="align-middle"
                    style="width: 200px;">
                    <d-button
                      theme="primary"
                      @click.prevent="editPrice(price)"
                      class="mr-2"
                      :title="$t('ui.edit')">
                      <i class="material-icons">edit</i>
                    </d-button>
                    <d-button
                      theme="danger"
                      :disabled="removeInProgress"
                      @click.prevent="removePrice(price.id)"
                      :title="$t('ui.delete')">
                      <i class="material-icons">delete</i>
                    </d-button>
                  </td>
                </tr>
                <tr v-if="prices.length === 0">
                  <td
                    colspan="8"
                    class="text-center">
                    {{ $t('price.noPricesToDisplay') }}&hellip;
                  </td>
                </tr>
              </tbody>
            </table>

            <div v-if="!loadedPrices" class="pb-3 pt-3 text-center">
              {{ $t('ui.dataLoading') }}&hellip;
            </div>

            <d-alert
              v-if="loadPricesError"
              show
              theme="warning">
              {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
            </d-alert>
          </d-tab>
        </d-tabs>
      </d-card>
    </d-form>

    <shipper-price-new-popup
      v-if="showAddPopup"
      :shipperID="this.id"
      :countries="countries"
      :currencies="currencies" />

    <shipper-price-edit-popup
      v-if="showEditPopup"
      :shipperID="this.id"
      :priceData="selectedPrice"
      :countries="countries"
      :currencies="currencies" />
  </d-container>
</template>

<script>
import CommonGetters from './../../utils/CommonGetters.js';
import FormUtils from './../../utils/FormUtils.js';
import ListUtils from './../../utils/ListUtils.js';
import ShipperPriceNewPopup from './ShipperPriceNewPopup.vue';
import ShipperPriceEditPopup from './ShipperPriceEditPopup.vue';

export default {
  name: 'shipper-edit',
  components: {
    'shipper-price-new-popup': ShipperPriceNewPopup,
    'shipper-price-edit-popup': ShipperPriceEditPopup
  },
  data() {
    return {
      id: 0,
      name: '',
      code: '',
      currencies: [],
      countries: [],
      loadedData: false,
      loadedCountries: false,
      loadedCurrencies: false,
      loadError: false,
      prices: [],
      loadedPrices: false,
      loadPricesError: false,
      showAddPopup: false,
      selectedPrice: 0,
      showEditPopup: false,
      removeInProgress: false
    };
  },
  computed: {
    allDataLoaded () {
      return this.loadedData && this.loadedCountries && this.loadedCurrencies;
    }
  },
  mounted () {
    FormUtils.loadItemData(this, {
      endpointBase: '/shippers/item/',
      instanceFields: ['name', 'code'],
      loadedKey: 'loadedData'
    });

    this.loadAdditionalData();
    this.loadPrices();
    this.$bus.$on('shipper-add-price-popup-close', this.closeAddPopup);
    this.$bus.$on('shipper-edit-price-popup-close', this.closeEditPopup);
    this.$bus.$on('shipper-reload-prices', this.loadPrices);
  },
  methods: {
    validateForm () {
      FormUtils.validate(this.$refs['shipper-edit-form'], this.submitForm);
    },
    submitForm () {
      FormUtils.submit({
        $http: this.$http,
        endpoint: '/shippers/update/' + this.id,
        formData: {
          name: this.name,
          code: this.code
        },
        successTitle: this.$t('shippers.editShipperSuccessTitle'),
        successText: this.$t('shippers.editShipperSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('shippers.editShipperErrorText'),
        successAction: () => {
          this.loadPrices();
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/countries/items',
        loadedKey: 'loadedCountries',
        outputKey: 'countries',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/currencies/items',
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError'
      });
    },
    loadPrices () {
      this.loadedPrices = false;

      FormUtils.loadAdditionalData(this, {
        endpoint: '/shippers/prices/items',
        loadedKey: 'loadedPrices',
        outputKey: 'prices',
        errorKey: 'loadPricesError',
        params: {
          where: {
            shipper_id: this.id
          }
        }
      });
    },
    addPrice () {
      this.showAddPopup = true;
    },
    editPrice (price) {
      this.selectedPrice = JSON.parse(JSON.stringify(price));
      this.showEditPopup = true;
    },
    removePrice (priceID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/shippers/prices/delete/',
        id: priceID,
        successTitle: this.$t('price.deletePriceSuccessTitle'),
        successText: this.$t('price.deletePriceSuccessText'),
        confirmTitle: this.$t('price.deletePriceConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('price.deletePriceErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadPrices();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    closeAddPopup () {
      this.showAddPopup = false;
    },
    closeEditPopup () {
      this.showEditPopup = false;
    },
    getCurrency (currencyID) {
      return CommonGetters.getCurrencyById(currencyID, this.currencies);
    },
    getCountry (countryID) {
      return CommonGetters.getCountryById(countryID, this.countries);
    }
  },
  beforeDestroy () {
    this.$bus.$off('shipper-add-price-popup-close', this.closeAddPopup);
    this.$bus.$off('shipper-edit-price-popup-close', this.closeEditPopup);
    this.$bus.$off('shipper-reload-prices', this.loadPrices);
  }
};
</script>
